import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {
  Aangifte,
  BokkzIbDataService,
  IncomeTaxEntity,
  OmschrijvingEnBedrag
} from "@snelstart-ventures/fiscaal/domain-logic";
import {FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  merge,
  Observable,
  shareReplay,
  Subscription,
  switchMap, take,
} from "rxjs";
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

type OmschrijvingEnBedragFormGroup = FormGroup<{
  omschrijving: FormControl<string | null>;
  bedrag: FormControl<number | null>;
}>;

@Component({
  selector: 'snelstart-venture-fiscaal-feature-alimentatie',
  templateUrl: './alimentatie.component.html',
  styleUrls: ['./alimentatie.component.scss'],
  imports: [
    SharedUiButtonModule,
    ReactiveFormsModule,
    SharedUiDetailsModule,
    SharedUiFormControlsModule,
    CommonModule
  ]
})
export class AlimentatieComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzIbDataService);
  private destroyRef = inject(DestroyRef);
  private changeSubscriptions = new Subscription();
  public isPartner = false;

  readonly incomeTaxEntity$: Observable<IncomeTaxEntity> =
    this.dataService.incomeTaxEntity$.pipe(
      filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  public formGroup = new FormGroup({
    ontvangenAlimentatie: new FormArray<OmschrijvingEnBedragFormGroup>([]),
    aftrekbareKostenAlimentatie: new FormArray<OmschrijvingEnBedragFormGroup>([]),
  });

  ngOnInit(): void {
    this.dataService.partnerAangifte$.pipe(
      takeUntilDestroyed(this.destroyRef),
      switchMap(partner => {
        this.isPartner = partner;
        return this.incomeTaxEntity$.pipe(
          take(1),
          map(entity => ({entity, partner}))
        );
      })
    ).subscribe(({entity, partner}) => {
      this.handleSwitch(entity, partner);
    });

    this.subscribeToChanges();
  }

  ngOnDestroy(): void {
    this.changeSubscriptions.unsubscribe();
  }

  private subscribeToChanges(): void {
    this.changeSubscriptions.unsubscribe();
    this.changeSubscriptions = new Subscription();

    this.changeSubscriptions.add(
      merge(
        this.formGroup.controls.ontvangenAlimentatie.valueChanges,
        this.formGroup.controls.aftrekbareKostenAlimentatie.valueChanges
      ).pipe(
        map(() => this.saveForm()),
        map(() => this.extractInputForSave()),
        distinctUntilChanged((prev, curr) =>
          JSON.stringify(prev) === JSON.stringify(curr)
        ),
        debounceTime(500),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe((curr) => {
        if (curr.some(row => row.bedrag !== null)) {
          this.dataService.calculateLatestIncomeTaxReturn().subscribe();
        }
      })
    );
  }

  private extractInputForSave(): { omschrijving: string | null; bedrag: number | null }[] {
    return [
      ...this.opbrengsten.getRawValue().map(item => ({
        omschrijving: item.omschrijving,
        bedrag: item.bedrag,
      })),
      ...this.kosten.getRawValue().map(item => ({
        omschrijving: item.omschrijving,
        bedrag: item.bedrag,
      })),
    ];
  }

  private handleSwitch(entity: IncomeTaxEntity, isPartner: boolean): void {
    const details = isPartner ? entity.aangifte?.partnerAangifte : entity.aangifte;
    if (details) {
      this.resetAndPatchForms(details);
    }
  }

  private resetAndPatchForms(details: any): void {
    const data = details.box1?.belastbarePeriodiekeUitkeringenEnVerstrekkingen;
    this.updateForm(data?.ontvangenAlimentatie || [], this.opbrengsten);
    this.updateForm(data?.aftrekbareKostenAlimentatie || [], this.kosten);
  }

  get opbrengsten(): FormArray {
    return this.formGroup.get('ontvangenAlimentatie') as FormArray;
  }

  get kosten(): FormArray {
    return this.formGroup.get('aftrekbareKostenAlimentatie') as FormArray;
  }

  addRijOpbrengsten(): void {
    this.opbrengsten.push(this.createFormGroup(), { emitEvent: false })
    this.saveForm();
  }

  addRijKosten(): void {
    this.kosten.push(this.createFormGroup(), { emitEvent: false })
    this.saveForm();
  }

  verwijderRijOpbrengsten(index: number): void {
    this.opbrengsten.removeAt(index, { emitEvent: false });
    this.formGroup.updateValueAndValidity({ emitEvent: false })
    this.formGroup.markAsDirty({emitEvent: false})
    this.saveForm();
  }

  verwijderRijKosten(index: number): void {
    this.kosten.removeAt(index, { emitEvent: false });
    this.formGroup.updateValueAndValidity({ emitEvent: false })
    this.formGroup.markAsDirty({emitEvent: false})
    this.saveForm();
  }

  createFormGroup(item?: OmschrijvingEnBedrag): FormGroup {
    return new FormGroup({
      omschrijving: new FormControl<string | null>(
        item?.omschrijving?.trim() ?? null,
        {validators: [Validators.required, Validators.minLength(2)]}),
      bedrag: new FormControl<number | null>(
        item?.bedrag ?? null,
        {validators: [Validators.required, Validators.min(1)]}
      ),
    });
  }

  private updateForm(list: OmschrijvingEnBedrag[], array: FormArray): void {
    const lijst = list || [];
    const formArray = array;

    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1, {emitEvent: false});
    }

    for (let i = 0; i < lijst.length; i++) {
      if (i < formArray.length) {
        const control = formArray.at(i) as FormGroup;
        control.patchValue(lijst[i], {emitEvent: false});
      } else {
        const formGroup = this.createFormGroup(lijst[i]);
        formArray.push(formGroup, {emitEvent: false});
      }
    }
  }

  saveForm(): void {
    this.formGroup.updateValueAndValidity({emitEvent: false});
    const rawValue = this.formGroup.getRawValue();

    const filterEmptyRows = (rows: { omschrijving: string | null, bedrag: number | null }[]) =>
      rows.filter(row => row.omschrijving?.trim() || row.bedrag !== null);

    const update = (aangifte: Aangifte) => {
      const ontvangenAlimentatie = filterEmptyRows(rawValue.ontvangenAlimentatie);
      const aftrekbareKostenAlimentatie = filterEmptyRows(rawValue.aftrekbareKostenAlimentatie);

      return {
        ...aangifte,
        box1: {
          ...aangifte.box1,
          belastbarePeriodiekeUitkeringenEnVerstrekkingen: {
            ...aangifte.box1?.belastbarePeriodiekeUitkeringenEnVerstrekkingen,
            ontvangenAlimentatie: ontvangenAlimentatie.length ? ontvangenAlimentatie : undefined,
            aftrekbareKostenAlimentatie: aftrekbareKostenAlimentatie.length ? aftrekbareKostenAlimentatie : undefined,
          },
        },
      };
    };

    this.dataService.patchIncomeTaxEntity((currentEntity) => {
      if (!currentEntity.aangifte) return currentEntity;

      const updatedAangifte = this.isPartner ? {
        ...currentEntity.aangifte,
        partnerAangifte: update(currentEntity.aangifte?.partnerAangifte!),
      } : update(currentEntity.aangifte);

      return { ...currentEntity, aangifte: updatedAangifte } as IncomeTaxEntity;
    });
  }
}
